import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/** Lightweight image lightbox with keyboard nav. */
export const Lightbox = ({ images, open, index, onClose, onNav }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose, onNav]);

  if (!open) return null;
  const current = images[index];
  if (!current) return null;

  return (
    <div
      data-testid="lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/90 lightbox-backdrop flex items-center justify-center p-4 md:p-10"
    >
      <button
        data-testid="lightbox-close"
        aria-label="Bezárás"
        onClick={onClose}
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
      >
        <X className="w-6 h-6" />
      </button>

      <button
        aria-label="Előző"
        onClick={(e) => {
          e.stopPropagation();
          onNav(-1);
        }}
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        aria-label="Következő"
        onClick={(e) => {
          e.stopPropagation();
          onNav(1);
        }}
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <figure
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-6xl w-full max-h-[88vh] flex items-center justify-center"
      >
        <img
          src={current.url || current}
          alt={current.label || ""}
          className="max-w-full max-h-[88vh] object-contain rounded-md shadow-2xl"
        />
        {current.label && (
          <figcaption className="absolute -bottom-12 left-0 right-0 text-center text-white/90 font-heading text-sm md:text-base">
            {current.label}
          </figcaption>
        )}
      </figure>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/60 tracking-widest">
        {index + 1} / {images.length}
      </div>
    </div>
  );
};

/** Hook helper to manage lightbox state for any gallery */
export const useLightbox = (images) => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
  };
  const close = () => setOpen(false);
  const nav = (dir) =>
    setIndex((i) => (i + dir + images.length) % images.length);
  return { open, index, openAt, close, nav };
};

export default Lightbox;
